import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import MarkdownBlock from './Chat/MarkdownBlock';
import clsxm from '@/lib/clsxm';

interface Faq {
  question: string;
  answer: string;
}

interface RoomSpecificFAQProps {
  faqs: Faq[];
  roomName: string;
}

export default function RoomSpecificFAQ({
  faqs,
  roomName,
}: RoomSpecificFAQProps) {
  const { t, lang } = useLocale('common');

  if (!faqs || faqs.length === 0 || lang !== 'en') {
    return null;
  }

  return (
    <div className='bg-gray-50 dark:bg-slate-900'>
      <div className='mx-auto max-w-7xl py-12 px-4 sm:py-16 sm:px-6 lg:px-8'>
        <div
          className='mx-auto max-w-3xl divide-y-2 divide-gray-200 dark:divide-slate-600'
          itemScope
          itemType='https://schema.org/FAQPage'
        >
          <h2 className='text-center text-3xl font-black tracking-tight text-gray-900 sm:text-4xl dark:text-slate-200'>
            {t('Frequently asked questions about')} {roomName}{' '}
            {t('Design', { defaultValue: 'Design' }).toLowerCase()}
          </h2>
          <dl className='mt-6 space-y-6 divide-y divide-gray-200 dark:divide-slate-600'>
            {faqs.map((faq, index) => (
              <Disclosure
                as='div'
                key={index}
                className='pt-6'
                itemScope
                itemProp='mainEntity'
                itemType='https://schema.org/Question'
              >
                {({ open }) => (
                  <>
                    <dt className='text-lg'>
                      <Disclosure.Button className='group/faq flex w-full items-start justify-between text-left text-gray-400 transition-colors duration-200 hover:text-primary-500 dark:text-slate-400 dark:hover:text-primary-400'>
                        <span
                          className='font-medium text-gray-900 transition-colors duration-200 group-hover/faq:text-primary-700 dark:text-slate-200 dark:group-hover/faq:text-primary-300'
                          itemProp='name'
                        >
                          {faq.question}
                        </span>
                        <span className='ml-6 flex h-7 items-center'>
                          <ChevronDownIcon
                            className={clsxm(
                              open ? '-rotate-180' : 'rotate-0',
                              'h-6 w-6 transform transition-transform duration-300 ease-out',
                            )}
                            aria-hidden='true'
                          />
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel
                      as='dd'
                      className='mt-2 pr-12'
                      itemScope
                      itemProp='acceptedAnswer'
                      unmount={false}
                      itemType='https://schema.org/Answer'
                    >
                      <div
                        className='text-base text-gray-500 dark:text-slate-400'
                        itemProp='text'
                      >
                        <MarkdownBlock
                          message={faq.answer.replace(/\n/g, '  \n')}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
